import React, { useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useLeague } from "../../hooks/useLeague";
import { useToast } from "../../context/ToastContext";
import { apiPatch, apiPost } from "../../utils/apiClient";
import { getGoalUrl, getGoalsUrl } from "../../config/api";
import { Game, Goal } from "../../types/models";
import EntryPanel, { EntryStep } from "./EntryPanel";
import PlayerGrid from "./PlayerGrid";
import OptionGrid from "./OptionGrid";
import ClockEntry from "./ClockEntry";
import { TeamRoster } from "./players";
import { Period, PERIOD_OPTIONS, formatPeriod } from "./periods";
import { ClockValue, clockError, formatClock, splitClock } from "./clock";

/** The order the steps are asked in. */
const FLOW = ["scorer", "assist1", "assist2", "period", "time"];

interface GoalEntryProps {
  game: Game;
  subtitle: string;
  rosters: TeamRoster[];
  editingGoal: Goal | null;
  onSaved: () => void;
  onCancel: () => void;
}

function GoalEntry({
  game,
  subtitle,
  rosters,
  editingGoal,
  onSaved,
  onCancel,
}: GoalEntryProps) {
  const auth = useAuth();
  const { currentLeague } = useLeague();
  const { showToast } = useToast();

  const [teamId, setTeamId] = useState<number | null>(
    editingGoal?.team_id ?? null,
  );
  const [scorerId, setScorerId] = useState<number | null>(
    editingGoal?.goal_player_id ?? null,
  );
  const [assist1, setAssist1] = useState<number | null>(
    editingGoal?.assist1_player_id ?? null,
  );
  const [assist2, setAssist2] = useState<number | null>(
    editingGoal?.assist2_player_id ?? null,
  );
  // An assist is answered once it has been chosen, including when it was
  // deliberately left empty, so its chip can show "None" rather than "Not
  // set". Editing an existing goal starts with both already answered.
  const [assist1Answered, setAssist1Answered] = useState(editingGoal !== null);
  const [assist2Answered, setAssist2Answered] = useState(editingGoal !== null);
  const [period, setPeriod] = useState<Period>(
    editingGoal?.period ?? "period1",
  );
  const [clock, setClock] = useState<ClockValue>(
    splitClock(editingGoal?.time_formatted),
  );
  const [activeStepKey, setActiveStepKey] = useState(FLOW[0]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const playersById = useMemo(
    () =>
      new Map(
        rosters.flatMap((roster) =>
          roster.players.map((player) => [player.id, player] as const),
        ),
      ),
    [rosters],
  );

  // Assists are credited to the scoring team, so they are picked from it alone.
  const scoringRosters = rosters.filter((roster) => roster.teamId === teamId);

  const goToNextStep = (key: string) => {
    const index = FLOW.indexOf(key);
    if (index >= 0 && index < FLOW.length - 1) {
      setActiveStepKey(FLOW[index + 1]);
    }
  };

  const playerChip = (
    playerId: number | null,
    answered: boolean,
  ): string | null => {
    if (!answered) {
      return null;
    }
    if (playerId === null) {
      return "None";
    }

    const player = playersById.get(playerId);
    return player
      ? `#${player.jerseyLabel} ${player.name}`
      : `Player ${playerId}`;
  };

  const handleScorerSelect = (playerId: number, selectedTeamId: number) => {
    setScorerId(playerId);
    setTeamId(selectedTeamId);

    if (selectedTeamId !== teamId) {
      // A different team's assists no longer apply.
      setAssist1(null);
      setAssist1Answered(false);
      setAssist2(null);
      setAssist2Answered(false);
    } else {
      // The scorer cannot also be credited with an assist on their own goal.
      if (assist1 === playerId) {
        setAssist1(null);
        setAssist1Answered(false);
      }
      if (assist2 === playerId) {
        setAssist2(null);
        setAssist2Answered(false);
      }
    }

    goToNextStep("scorer");
  };

  const handleAssist1Select = (playerId: number | null) => {
    setAssist1(playerId);
    setAssist1Answered(true);
    goToNextStep("assist1");
  };

  const handleAssist2Select = (playerId: number | null) => {
    setAssist2(playerId);
    setAssist2Answered(true);
    goToNextStep("assist2");
  };

  const renderAssistStep = (
    selected: number | null,
    answered: boolean,
    excluded: (number | null)[],
    onSelect: (playerId: number | null) => void,
  ) => {
    if (teamId === null) {
      return <p className="ee-empty">Choose the goal scorer first.</p>;
    }

    return (
      <PlayerGrid
        rosters={scoringRosters}
        selectedPlayerId={selected}
        onSelect={(playerId) => onSelect(playerId)}
        excludedPlayerIds={excluded.filter((id): id is number => id !== null)}
        noneLabel="No assist"
        noneSelected={answered && selected === null}
        onSelectNone={() => onSelect(null)}
      />
    );
  };

  const handleSave = () => {
    setErrorMessage(null);

    if (scorerId === null || teamId === null) {
      setErrorMessage("Choose who scored.");
      setActiveStepKey("scorer");
      return;
    }

    const invalidClock = clockError(clock);
    if (invalidClock) {
      setErrorMessage(invalidClock);
      setActiveStepKey("time");
      return;
    }

    setIsSubmitting(true);

    const requestBody = {
      goal: {
        team_id: teamId,
        goal_player_id: scorerId,
        assist1_player_id: assist1,
        assist2_player_id: assist2,
        period,
        time: formatClock(clock),
      },
    };

    const url = editingGoal ? getGoalUrl(editingGoal.id) : getGoalsUrl(game.id);
    const apiFn = editingGoal ? apiPatch : apiPost;

    apiFn(url, requestBody, {
      token: auth.token,
      ...(currentLeague?.abbr ? { leagueAbbr: currentLeague.abbr } : {}),
    })
      .then(() => {
        showToast(editingGoal ? "Goal updated" : "Goal added", "success");
        onSaved();
      })
      .catch((error) => {
        setErrorMessage(error.message || "An error occurred");
        setIsSubmitting(false);
      });
  };

  const steps: EntryStep[] = [
    {
      key: "scorer",
      chipLabel: "Goal",
      chipValue: playerChip(scorerId, scorerId !== null),
      prompt: "Who scored?",
      render: () => (
        <PlayerGrid
          rosters={rosters}
          selectedPlayerId={scorerId}
          onSelect={handleScorerSelect}
        />
      ),
    },
    {
      key: "assist1",
      chipLabel: "Assist 1",
      chipValue: playerChip(assist1, assist1Answered),
      prompt: "Who had the first assist?",
      render: () =>
        renderAssistStep(
          assist1,
          assist1Answered,
          [scorerId, assist2],
          handleAssist1Select,
        ),
    },
    {
      key: "assist2",
      chipLabel: "Assist 2",
      chipValue: playerChip(assist2, assist2Answered),
      prompt: "Who had the second assist?",
      render: () =>
        renderAssistStep(
          assist2,
          assist2Answered,
          [scorerId, assist1],
          handleAssist2Select,
        ),
    },
    {
      key: "period",
      chipLabel: "Period",
      chipValue: formatPeriod(period),
      prompt: "Which period?",
      render: () => (
        <OptionGrid
          options={PERIOD_OPTIONS}
          selected={period}
          onSelect={(value) => {
            setPeriod(value);
            goToNextStep("period");
          }}
        />
      ),
    },
    {
      key: "time",
      chipLabel: "Time",
      chipValue:
        clock.minutes === "" && clock.seconds === ""
          ? null
          : formatClock(clock),
      prompt: "What did the clock say?",
      render: () => <ClockEntry value={clock} onChange={setClock} />,
    },
  ];

  return (
    <EntryPanel
      title={editingGoal ? "Edit goal" : "Add goal"}
      subtitle={subtitle}
      steps={steps}
      activeStepKey={activeStepKey}
      onStepSelect={setActiveStepKey}
      errorMessage={errorMessage}
      isSubmitting={isSubmitting}
      canSave={scorerId !== null}
      saveLabel="Save goal"
      onSave={handleSave}
      onCancel={onCancel}
    />
  );
}

export default GoalEntry;
